import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import withPermissionProtection from 'hoc/withPermissionProtection';

import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import EyeIcon from '@material-ui/icons/Visibility';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import Typography from 'components/CncUI/Typography';
import TextField from 'components/CncUI/TextField';
import DatePicker from 'components/CncUI/DatePicker';
import Button from 'components/CncUI/Button';
import DataTable from 'components/CncUI/DataTable';
import Loader from 'components/Loader';
import FileViewer from 'components/FileViewer';
import FileDownloader from 'components/FileDownloader';
import Snackbar from 'components/CncUI/Snackbar';

import useCountry from 'hooks/useCountry';
import useBrowserMatchVersion from 'hooks/useBrowserMatchVersion';
import useGetPaystubs from './hooks/useGetPaystubs';
import useGetPaystubImage from './hooks/useGetPaystubImage';
import formatUSD from 'utilities/formatUSD';
import Logger from 'utilities/logger';
import filterPaystubs from './utils/filterPaystubs';

import COUNTRIES from './utils/countries';
import STATES from './utils/states';
import Enums from 'constants/componentData/fmsEnums';

const useStyles = makeStyles(({ breakpoints, palette }) => ({
  header: {
    marginTop: 40,
    marginBottom: 0,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
  description: {
    '& a': {
      color: palette.cncUI.misc.link,
    },
  },
  table: {
    marginTop: 8,
  },
  clickable: {
    cursor: 'pointer',
  },
  searchContainer: {
    [breakpoints.only('xs')]: {
      display: 'flex',
      flex: '1',
    },
  },
  search: {
    minWidth: 310,
    [breakpoints.only('xs')]: {
      width: '100%',
    },
  },
  from: {
    width: 180,
    [breakpoints.only('xs')]: {
      width: '100%',
    },
  },
  to: {
    width: 180,
    [breakpoints.only('xs')]: {
      width: '100%',
      marginTop: 16,
    },
  },
  clear: {
    marginTop: 15,
  },
  snackbar: {
    [breakpoints.up('md')]: {
      top: 140,
    },
  },
}));

const Paystubs = () => {
  const logger = Logger('Paystubs');
  const classes = useStyles();
  const { country } = useCountry();

  const [filters, setFilters] = useState({
    search: '',
    from: null,
    to: null,
    range: {},
  });
  const [view, setView] = useState('');
  const [download, setDownload] = useState('');
  const [paystubs, loading, error] = useGetPaystubs(country);
  const getPaystubImage = useGetPaystubImage();
  const [browserMatch] = useBrowserMatchVersion(
    ['safari', 'mobile safari'],
    ['<13', '<13'],
  );
  const [alert, setAlert] = useState({
    message: '',
    show: false,
  });
  const notify = data => setAlert(old => ({ ...old, ...data }));
  const checkLabel = country === 'CAN' ? 'Cheque' : 'Check';

  useEffect(() => {
    if (error) {
      if (error.message && error.message.includes('Failed to fetch')) {
        const message = `Your pay stubs are still loading and may take up to ${
          country === 'CAN' ? 'another' : 'a'
        } minute`;
        notify({ message: message, show: true });
      }
      logger.error(error);
    }
  }, [error]);

  const clearRange = () => setFilters({ ...filters, from: null, to: null });
  const date = field => paystub => paystub[field];
  const usd = field => paystub => formatUSD(paystub[field]);
  const fromSource = field => paystub => {
    const { dataSource } = Enums;
    switch (paystub[field]) {
      case dataSource.cnc:
        return 'CastAndCrew';
      case dataSource.caps:
        return 'CAPS';
      case dataSource.ms:
        return 'Media Services';
      default:
        return 'Unknown';
    }
  };

  const formatWorkState = ({ workState, workCountry }) =>
    STATES[workCountry || 'US']?.[workState] || workState;

  const formatWorkCountry = ({ workCountry }) =>
    COUNTRIES[workCountry] || workCountry;

  const filtered = filterPaystubs(paystubs, filters);

  const columns = [
    {
      field: 'checkNumber',
      label: `${checkLabel} Number`,
    },
    {
      field: 'effectiveDate',
      label: `${checkLabel} date`,
      value: date('effective'),
    },
    {
      field: 'weekEndingDate',
      label: 'Week Ending Date',
      value: date('weekEnding'),
    },
    { field: 'checkGross', label: 'Gross Pay', value: usd('checkGross') },
    { field: 'checkNet', label: 'Net Pay', value: usd('checkNet') },
    { field: 'projectTitle', label: 'Project' },
    {
      field: 'workState',
      label: country === 'CAN' ? 'Work Province' : 'Work State',
      value: formatWorkState,
    },
    {
      field: 'workCountry',
      label: 'Work Country',
      value: formatWorkCountry,
    },
    {
      field: 'dataSource',
      label: 'Payroll System',
      value: fromSource('dataSource'),
    },
    {
      field: 'actions',
      label: ' ',
      sortable: false,
      headerProps: { style: { minWidth: 100 } },
      value: ({ checkSequence }) => (
        <Grid
          container
          spacing={2}
          alignItems="center"
          justify="flex-end"
          id={`Actions-${checkSequence}`}
        >
          {!browserMatch && (
            <Grid item>
              <EyeIcon
                id={`View-${checkSequence}`}
                title={`View ${checkLabel}`}
                className={classes.clickable}
                onClick={() => setView(checkSequence)}
              />
            </Grid>
          )}
          <Grid item>
            <DownloadIcon
              id={`Download-${checkSequence}`}
              title={`Download ${checkLabel}`}
              className={classes.clickable}
              onClick={() => setDownload(checkSequence)}
            />
          </Grid>
        </Grid>
      ),
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6} id="Header">
        <Typography variant="h2" className={classes.header}>
          My Pay Stubs
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Snackbar
          className={classes.snackbar}
          title={alert.message}
          open={alert.show}
          onClose={() => notify({ show: false })}
          position="top-center"
          hideDuration={30000}
          variant="info"
        >
          {
            <>
              You can navigate to other sections of the site without
              interrupting this process <br /> while you wait. Otherwise, the
              page will automatically reload upon completion.
            </>
          }
        </Snackbar>
      </Grid>
      <Grid item xs={12} className={classes.description} id="Description">
        <Typography variant="body2" size="long">
          If you have any questions about your previous pay stubs, please visit
          us at{' '}
          <Link
            id="SupportLink"
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.castandcrew.com/support/production-employee-support/"
            color="inherit"
          >
            https://www.castandcrew.com/support/production-employee-support/
          </Link>{' '}
          for our FAQ’s.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item className={classes.searchContainer}>
            <TextField
              light
              fullWidth
              id="SearchField"
              label="Filter pay stubs"
              value={filters.search}
              className={classes.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              startIcon={<SearchIcon />}
              onClear={() => setFilters({ ...filters, search: '' })}
            />
          </Grid>
          <Grid item>
            <Grid container spacing={1} alignItems="flex-start">
              <Grid item>
                <DatePicker
                  light
                  id="DateFromField"
                  label={`${checkLabel} Date From`}
                  value={filters.from}
                  className={classes.from}
                  onChange={date => setFilters({ ...filters, from: date })}
                  inputProps={{
                    pattern: '[0-9]*',
                    inputMode: 'numeric',
                  }}
                />
                <DatePicker
                  light
                  id="DateToField"
                  label={`${checkLabel} Date To`}
                  value={filters.to}
                  className={classes.to}
                  onChange={date => setFilters({ ...filters, to: date })}
                  inputProps={{
                    pattern: '[0-9]*',
                    inputMode: 'numeric',
                  }}
                />
              </Grid>
              <Grid item>
                <Button
                  tertiary
                  id="ClearDatesButton"
                  startIcon={<ClearIcon />}
                  onClick={clearRange}
                  className={classes.clear}
                >
                  Clear dates
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {loading ? (
          <Loader message="Loading your pay stubs..." />
        ) : (
          <DataTable
            id="PaystubsTable"
            transparent
            columns={columns}
            data={filtered}
            sort="effectiveDate"
            order="desc"
            emptyRows="There are no pay stubs to display."
            className={classes.table}
          />
        )}
      </Grid>
      {view && (
        <FileViewer
          fileFetcher={getPaystubImage}
          config={{ checkSequence: view, country }}
          onClose={() => setView('')}
        />
      )}
      {download && (
        <FileDownloader
          urlFetcher={getPaystubImage}
          config={{ checkSequence: download, country }}
          onClose={() => setDownload('')}
          downloadName={`CastAndCrew${checkLabel}-${download}.pdf`}
        />
      )}
    </Grid>
  );
};

export default withPermissionProtection('paystubs:read')(Paystubs);
